<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>프롬북 · 이벤트 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small outlined dark class="mr-2">총 {{ total.toLocaleString() }}건</v-chip>
        <v-btn small outlined dark @click="openCreateDialog">
          <v-icon left small>mdi-plus</v-icon>새 이벤트
        </v-btn>
      </v-toolbar>

      <!-- 필터 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.event_type"
              :items="eventTypeOptions"
              label="이벤트 타입"
              outlined dense hide-details clearable
            ></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.is_active"
              :items="activeOptions"
              label="활성 여부"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="1">
            <v-btn color="primary" depressed block @click="load">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- 이벤트 리스트 -->
      <v-card-text class="pa-0 pt-2">
        <div style="overflow-x: auto;">
          <v-data-table
            :headers="headers"
            :items="list"
            :items-per-page="50"
            :footer-props="{'items-per-page-options':[50, 100, 200]}"
            :loading="loading"
            :server-items-length="total"
            class="elevation-0"
          >
            <template v-slot:item.event_type="{ item }">
              <v-chip x-small
                :color="item.event_type === 'banner' ? 'blue' : item.event_type === 'popup' ? 'purple' : 'deep-orange'"
                dark>
                {{ item.event_type }}
              </v-chip>
            </template>
            <template v-slot:item.image_path="{ item }">
              <v-avatar size="36" tile v-if="item.image_path">
                <v-img :src="item.image_path"></v-img>
              </v-avatar>
              <v-icon v-else color="grey lighten-1">mdi-image-off</v-icon>
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-switch
                v-model="item.is_active"
                dense hide-details
                @change="toggleActive(item)"
                class="mt-0 pt-0"
              ></v-switch>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click="openEditDialog(item)" title="수정">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="deleteEvent(item)" title="삭제">
                <v-icon small color="red">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
        <v-container v-if="!loading && list.length === 0" class="text-center py-8">
          <v-icon size="56" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <p class="text-body-1 grey--text mt-3">이벤트가 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 이벤트 등록/수정 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="600" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-calendar-star</v-icon>
          이벤트 {{ form.id ? '수정' : '등록' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-select
            v-model="form.event_type"
            :items="eventTypeOptions"
            label="이벤트 타입"
            outlined dense class="mb-3"
          ></v-select>
          <v-text-field
            v-model="form.title"
            label="제목"
            outlined dense class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="form.image_path"
            label="이미지 경로 (URL)"
            outlined dense class="mb-3"
          ></v-text-field>
          <v-text-field
            v-model="form.link_url"
            label="링크 URL"
            outlined dense class="mb-3"
          ></v-text-field>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.start_at"
                label="시작일 (YYYY-MM-DD)"
                outlined dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.end_at"
                label="종료일 (YYYY-MM-DD)"
                outlined dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-switch v-model="form.is_active" label="활성" inset color="green"></v-switch>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="form.sort_order"
                label="정렬 순서"
                outlined dense type="number"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">취소</v-btn>
          <v-btn color="primary" depressed @click="saveEvent" :loading="saving">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { prombook } from '../../api'

export default {
  name: 'Prombook_EventList',
  data() {
    return {
      loading: false,
      saving: false,
      total: 0,
      list: [],
      filter: {
        event_type: null,
        is_active: null,
        page: 1,
        page_size: 50,
      },
      dialog: false,
      form: {
        id: null,
        event_type: 'banner',
        title: '',
        image_path: '',
        link_url: '',
        start_at: '',
        end_at: '',
        is_active: true,
        sort_order: 0,
      },
      eventTypeOptions: [
        { text: 'banner', value: 'banner' },
        { text: 'popup', value: 'popup' },
        { text: 'fullscreen', value: 'fullscreen' },
      ],
      activeOptions: [
        { text: '전체', value: null },
        { text: '활성', value: true },
        { text: '비활성', value: false },
      ],
      headers: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: '타입', value: 'event_type', width: '100px' },
        { text: '제목', value: 'title' },
        { text: '이미지', value: 'image_path', width: '70px', sortable: false },
        { text: '시작일', value: 'start_at', width: '120px' },
        { text: '종료일', value: 'end_at', width: '120px' },
        { text: '활성', value: 'is_active', width: '80px' },
        { text: '순서', value: 'sort_order', width: '70px' },
        { text: '액션', value: 'actions', width: '100px', sortable: false },
      ],
    }
  },
  methods: {
    async load() {
      try {
        this.loading = true
        const r = await prombook.list_event(
          this.filter.event_type,
          this.filter.is_active,
          this.filter.page,
          this.filter.page_size
        )
        this.list = r.event_list || []
        this.total = r.total || 0
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    openCreateDialog() {
      this.form = { id: null, event_type: 'banner', title: '', image_path: '', link_url: '', start_at: '', end_at: '', is_active: true, sort_order: 0 }
      this.dialog = true
    },
    openEditDialog(item) {
      this.form = JSON.parse(JSON.stringify(item))
      this.dialog = true
    },
    async saveEvent() {
      if (!this.form.title.trim()) {
        alert('제목을 입력해주세요.')
        return
      }
      try {
        this.saving = true
        await prombook.save_event({ ...this.form })
        alert('저장되었습니다.')
        this.dialog = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('저장에 실패했습니다.')
      } finally {
        this.saving = false
      }
    },
    async toggleActive(item) {
      try {
        await prombook.save_event({ id: item.id, is_active: item.is_active })
      } catch (e) {
        console.error(e)
        item.is_active = !item.is_active // 롤백
      }
    },
    async deleteEvent(item) {
      if (!confirm(`이벤트 [${item.title}]을 삭제하시겠습니까?`)) return
      try {
        await prombook.delete_event(item.id)
        alert('삭제되었습니다.')
        this.load()
      } catch (e) {
        console.error(e)
        alert('삭제에 실패했습니다.')
      }
    },
  },
  mounted() {
    this.load()
  },
}
</script>
